import type { User } from 'firebase/auth';
import * as React from 'react';

import { getIntercomUserHash } from '@gitbook/app-analytics/client';
import { getUserDisplayName } from '@gitbook/app-database';
import { webConfig } from '@gitbook/env';
import { head } from '@gitbook/util-react-head';
import { useEventCallback } from '@gitbook/util-react-hooks';

import { apiClient } from './api';
import { useCurrentUser } from './auth/provider';
import type { ContactSupportFn } from './support-types';

let intercomLoadPromise: Promise<void> | null = null;
let unreadCount = 0;
const unreadCountListeners = new Set<(count: number) => void>();
const intercomMessengerTokens = new Map<string, Promise<{ token: string; expiresAt: number }>>();
const INTERCOM_MESSENGER_TOKEN_REFRESH_BUFFER_SECONDS = 60;

/**
 * Return a callback to start contacting the support.
 */
export function useContactSupportIntercom(): ContactSupportFn {
    const user = useCurrentUser();

    React.useEffect(() => {
        if (!intercomLoadPromise) {
            return;
        }
        let cancelled = false;
        void (async () => {
            await intercomLoadPromise;
            if (cancelled || !window.Intercom) {
                return;
            }
            if (!user) {
                // If the user has logged out, we clear its Intercom browser session
                window.Intercom('shutdown');
                return;
            }

            // Otherwise, we update the user's information
            const params = await getIntercomUserParams(user);
            if (cancelled) {
                return;
            }
            window.Intercom?.('update', params);
        })();
        return () => {
            cancelled = true;
        };
    }, [user]);

    return useEventCallback(async () => {
        await loadIntercomMessenger(user);

        if (!window.Intercom) {
            // If Intercom Messenger is not available, we fallback to an email
            window.open('mailto:support@gitbook.com');
            return;
        }

        // If there are unread messages, jump straight to the conversation list
        // so the user sees the new reply rather than the Messenger home screen.
        window.Intercom(unreadCount > 0 ? 'showMessages' : 'show');
    });
}

/**
 * Subscribe to the unread message count from the Intercom Messenger.
 */
export function useIntercomUnreadCount(): number {
    const user = useCurrentUser();
    const [count, setCount] = React.useState(unreadCount);

    // Eagerly boot the Intercom Messenger on idle after mount, and subscribe
    // to count updates while mounted.
    React.useEffect(() => {
        unreadCountListeners.add(setCount);
        if (user) {
            if ('requestIdleCallback' in window) {
                window.requestIdleCallback(() => loadIntercomMessenger(user));
            } else {
                void loadIntercomMessenger(user);
            }
        }
        return () => {
            unreadCountListeners.delete(setCount);
        };
    }, [user]);

    return count;
}

function loadIntercomMessenger(user: User | null): Promise<void> {
    if (intercomLoadPromise) {
        return intercomLoadPromise;
    }

    intercomLoadPromise = new Promise<void>((resolve) => {
        if (process.env.GITBOOK_PREDICTABLE_UI || !webConfig.intercom.appId) {
            resolve();
            return;
        }

        head.writeAsync({
            type: 'script',
            src: `https://widget.intercom.io/widget/${webConfig.intercom.appId}`,
            onLoad: async () => {
                if (window.Intercom) {
                    const intercomUserParams = await getIntercomUserParams(user);
                    window.Intercom('boot', {
                        app_id: webConfig.intercom.appId,
                        hide_default_launcher: true,
                        ...intercomUserParams,
                    });
                    registerIntercomListeners();
                }
                resolve();
            },
        });
    });

    return intercomLoadPromise;
}

let listenersRegistered = false;

/**
 * Register listeners on the Intercom Messenger to drive the unread badge.
 */
function registerIntercomListeners(): void {
    if (listenersRegistered || !window.Intercom) {
        return;
    }
    listenersRegistered = true;

    window.Intercom('onUnreadCountChange', (nextCount: number) => {
        if (nextCount === unreadCount) {
            return;
        }
        unreadCount = nextCount;
        unreadCountListeners.forEach((listener) => listener(nextCount));
    });
}

async function getIntercomUserParams(user: User | null) {
    if (!user) {
        return {};
    }

    const authentication = await getIntercomAuthentication(user);
    return {
        email: user.email,
        user_id: user.uid,
        created_at: user.metadata.creationTime,
        name: getUserDisplayName(user),
        ...authentication,
    };
}

async function getIntercomAuthentication(
    user: User
): Promise<{ intercom_user_jwt: string } | { user_hash: string | null }> {
    if (webConfig.intercom.messengerJWTEnabled) {
        try {
            return {
                intercom_user_jwt: await getIntercomMessengerToken(user.uid),
            };
        } catch (_error) {
            // Fall through to the existing identity verification while JWTs are rolled out.
        }
    }

    return {
        user_hash: await getIntercomUserHash(user),
    };
}

async function getIntercomMessengerToken(userId: string): Promise<string> {
    const existingRequest = intercomMessengerTokens.get(userId);
    if (existingRequest) {
        const existingToken = await existingRequest;
        const refreshAt = existingToken.expiresAt - INTERCOM_MESSENGER_TOKEN_REFRESH_BUFFER_SECONDS;
        if (refreshAt > Date.now() / 1000) {
            return existingToken.token;
        }
    }

    const request = apiClient.users.createIntercomMessengerToken().then(({ data }) => data);
    intercomMessengerTokens.set(userId, request);

    try {
        return (await request).token;
    } catch (error) {
        if (intercomMessengerTokens.get(userId) === request) {
            intercomMessengerTokens.delete(userId);
        }
        throw error;
    }
}
